import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teamMembers } from '@/mocks/team';

const SERVICE_COUNT = 10;

export default function ContactSection() {
  const { t } = useTranslation();
  const [charCount, setCharCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      await fetch('https://readdy.ai/api/form/d76pprdr5g02gnd6a510', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <i className="ri-phone-fill text-blue-500 text-xs"></i>
            {t('contact.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('contact.title')} <span className="text-blue-600">{t('contact.highlight')}</span>
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-lg mx-auto">{t('contact.subtitle')}</p>
        </div>

        {/* Quick contact cards — always visible */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {teamMembers.map((m) => (
            <div key={m.id} className="bg-blue-50 rounded-2xl p-4 sm:p-5 border border-blue-100">
              <div className="flex sm:flex-col items-center sm:items-center gap-4 sm:gap-2">
                {/* Avatar */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden shrink-0 border-3 border-white sm:mx-auto sm:mb-1 border-2">
                  <img src={m.avatar} alt={m.name} className="w-full h-full object-cover object-top" />
                </div>
                {/* Info */}
                <div className="flex-1 sm:text-center">
                  <p className="font-extrabold text-gray-900 text-sm sm:text-base leading-tight">{m.name}</p>
                  <p className="text-gray-500 text-xs mb-1">Usta</p>
                  <p className="text-blue-600 text-sm font-bold mb-3">{m.phone}</p>
                  <div className="flex sm:flex-col gap-2">
                    <a
                      href={m.phoneHref}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-phone-fill text-xs"></i> Ara
                    </a>
                    <a
                      href={m.whatsapp}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-xs font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-whatsapp-line text-xs"></i> WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
          {/* Form */}
          <div className="bg-white border border-blue-100 rounded-2xl p-5 md:p-8">
            <h3 className="font-extrabold text-gray-900 text-base md:text-lg mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('contact.form.title')}
            </h3>

            {submitted ? (
              <div className="flex flex-col items-center text-center py-10">
                <span className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </span>
                <p className="font-bold text-gray-900 text-base mb-1">{t('contact.success.title')}</p>
                <p className="text-gray-500 text-sm mb-5">{t('contact.success.sub')}</p>
                <button onClick={() => setSubmitted(false)} className="text-blue-600 text-sm font-semibold hover:underline cursor-pointer">
                  {t('contact.success.back')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('contact.form.name')}</label>
                    <input type="text" name="adSoyad" required placeholder={t('contact.form.namePH')}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{t('contact.form.phone')}</label>
                    <input type="tel" name="telefon" required placeholder={t('contact.form.phonePH')}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors" />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('contact.form.email')}</label>
                  <input type="email" name="email" placeholder={t('contact.form.emailPH')}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors" />
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('contact.form.serviceType')}</label>
                  <select name="hizmetTuru"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors">
                    {Array.from({ length: SERVICE_COUNT + 1 }, (_, i) => (
                      <option key={i} value={i === 0 ? '' : t(`contact.svc.${i}`)}>{t(`contact.svc.${i}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{t('contact.form.message')}</label>
                  <textarea name="mesaj" required rows={4} maxLength={500}
                    placeholder={t('contact.form.messagePH')}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors resize-none"
                    onChange={(e) => {
                      if (e.target.value.length > 500) e.target.value = e.target.value.slice(0, 500);
                      setCharCount(e.target.value.length);
                    }}
                  />
                  <p className="text-right text-xs text-gray-400 mt-0.5">{charCount}/500</p>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-extrabold py-3.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
                  {loading ? t('contact.form.sending') : t('contact.form.submit')}
                </button>
              </form>
            )}
          </div>

          {/* Right info + map */}
          <div className="flex flex-col gap-4">
            {/* Info */}
            <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100 flex flex-col gap-4">
              {[
                { icon: 'ri-map-pin-2-fill', label: t('contact.loc.label'), value: t('contact.loc.value') },
                { icon: 'ri-time-fill', label: t('contact.hours.label'), value: t('contact.hours.value') },
                { icon: 'ri-whatsapp-fill', label: t('contact.wa.label'), value: t('contact.wa.value') },
              ].map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center bg-blue-600 rounded-xl shrink-0">
                    <i className={`${info.icon} text-white text-base`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold">{info.label}</p>
                    <p className="text-sm text-gray-900 font-medium">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>


          </div>
        </div>
      </div>
    </section>
  );
}
